import type { OrgNode, OrgNodeState, OrgNodeType } from './orgApi'

export type OfficeCapability = 'hasEmployees' | 'costCenter' | 'holdsStock'

export const TOP_LEVEL = 'top-level'

export const ORG_STATE_COLOR: Record<OrgNodeState, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  CLOSED: 'default',
}

const OFFICE_CAPABILITIES: readonly OfficeCapability[] = ['hasEmployees', 'costCenter', 'holdsStock']

const CODE_MAX_LENGTH = 64

function findType(types: readonly OrgNodeType[], kind: string): OrgNodeType | undefined {
  return types.find((type) => type.code === kind)
}

export function isOfficeType(type: OrgNodeType): boolean {
  return !type.settlementParty
}

export function isOfficeNode(node: OrgNode, types: readonly OrgNodeType[]): boolean {
  const type = findType(types, node.kind)
  return type === undefined || isOfficeType(type)
}

export function officesFirst<T extends OrgNode>(nodes: readonly T[], types: readonly OrgNodeType[]): T[] {
  return [...nodes].sort((a, b) => Number(!isOfficeNode(a, types)) - Number(!isOfficeNode(b, types)))
}

export function headOfficeTypes(types: readonly OrgNodeType[]): OrgNodeType[] {
  const offices = types.filter(isOfficeType)
  const topLevel = offices.filter((type) => type.allowedParents.length === 0)
  return topLevel.length > 0 ? topLevel : offices
}

export function childTypes(types: readonly OrgNodeType[], parentKind: string): OrgNodeType[] {
  return types.filter((type) => isOfficeType(type) && type.allowedParents.includes(parentKind))
}

export function typesUnder(types: readonly OrgNodeType[], parent: OrgNode | null): OrgNodeType[] {
  return parent === null ? headOfficeTypes(types) : childTypes(types, parent.kind)
}

export function officeCapabilities(type: OrgNodeType): OfficeCapability[] {
  return OFFICE_CAPABILITIES.filter((capability) => type[capability])
}

export function typesForExisting(
  types: readonly OrgNodeType[],
  nodes: readonly OrgNode[],
  node: OrgNode,
): OrgNodeType[] {
  const current = findType(types, node.kind)
  if (current?.settlementParty) return [current]
  const parent = node.parentId === null ? null : nodes.find((item) => item.id === node.parentId)
  const allowed = parent === undefined ? types.filter(isOfficeType) : typesUnder(types, parent)
  return current === undefined || allowed.includes(current) ? allowed : [current, ...allowed]
}

export function describeType(type: OrgNodeType, t: (key: string) => string): string {
  if (type.settlementParty) return t('org.can.settlement')
  const parts = officeCapabilities(type).map((capability) => t(`org.can.${capability}`))
  return parts.length > 0 ? parts.join(' · ') : t('org.can.nothing')
}

export function canHoldChildren(node: OrgNode, types: readonly OrgNodeType[]): boolean {
  return node.state !== 'CLOSED' && isOfficeNode(node, types) && childTypes(types, node.kind).length > 0
}

export function officeRoots(nodes: readonly OrgNode[], types: readonly OrgNodeType[]): OrgNode[] {
  const ids = new Set(nodes.map((node) => node.id))
  return nodes.filter(
    (node) =>
      (node.parentId === null || !ids.has(node.parentId)) &&
      node.state !== 'CLOSED' &&
      isOfficeNode(node, types),
  )
}

export function defaultLocation(
  nodes: readonly OrgNode[],
  types: readonly OrgNodeType[],
  start: OrgNode | null,
): string | null {
  if (start !== null && canHoldChildren(start, types)) return start.id
  const roots = officeRoots(nodes, types)
  const only = roots.length === 1 ? roots[0] : undefined
  return only !== undefined && canHoldChildren(only, types) ? only.id : null
}

export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’ʻʼ`]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, CODE_MAX_LENGTH)
    .replace(/-+$/, '')
}

function withSuffix(base: string, n: number): string {
  const suffix = `-${n}`
  return `${base.slice(0, CODE_MAX_LENGTH - suffix.length).replace(/-+$/, '')}${suffix}`
}

export function suggestCode(
  name: string,
  typeCode: string | null,
  existingCodes: Iterable<string>,
): string {
  const taken = new Set([...existingCodes].map((code) => code.toLowerCase()))
  const slug = slugify(name)

  if (/[a-z]/.test(slug) && slug.length >= 2) {
    if (!taken.has(slug)) return slug
    for (let n = 2; ; n++) {
      const candidate = withSuffix(slug, n)
      if (!taken.has(candidate)) return candidate
    }
  }

  if (typeCode === null || name.trim() === '') return ''
  const base = typeCode.toLowerCase()
  for (let n = 1; ; n++) {
    const candidate = withSuffix(base, n)
    if (!taken.has(candidate)) return candidate
  }
}
